/**
 * DLE 評価アンケートの型定義と設問文の SSOT（表示側）。
 *
 * 旧 Google Form「DLE評価」の設問文・選択肢説明をそのまま持つ。**シートへ転記するのは
 * 評点の数値だけ**で、ここの文言は表示専用。文言を直しても集計 (`mid_点数化` 以降) は
 * 壊れないが、点の意味が変わるので安易に書き換えないこと。
 *
 * 旧 Form の第 4 問「総合テストのレビューで、DLEは具体的な要件漏れやテスト不備を
 * 指摘してくれましたか？」は廃止した。設問は 4 問で、**各問にコメント欄が 1 つ**付く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価アンケートのタスク一覧・回答入力の型と、設問 4 問の文言・選択肢定義
 */

/** 回答可能になった母票ステータス。 @graph-connects none */
export type DleParentStatus = '開発完了' | '結合テスト完了' | '総合テスト完了' | '完了';

/** 評点 (順序尺度 1〜5)。 @graph-connects none */
export type DleScore = 1 | 2 | 3 | 4 | 5;

/** 評点の選択肢（低い順）。目盛バーの段の並びになる。 @graph-connects none */
export const DLE_SCORES: readonly DleScore[] = [1, 2, 3, 4, 5];

/** 1 軸ぶんの回答。`null` は「該当なし」。 @graph-connects none */
export type DleAnswerValue = DleScore | null;

/** 設問軸のキー。シートの得点列と 1:1。 @graph-connects none */
export type DleAxisKey = 'firstResponse' | 'sparring' | 'responseSpeed' | 'intent';

/** 設問 1 問の表示定義。 @graph-connects none */
export interface DleQuestion {
  key: DleAxisKey;
  /** 設問文（旧 Form の見出しそのまま） */
  title: string;
  /** 「この項目の目的」。何を測っているのかを 1 段落で示す */
  purpose: string;
  /** 評点 1〜5 の説明（低い順。目盛バーのラベル下に出す凡例と対応） */
  scaleLabels: Readonly<Record<DleScore, string>>;
  /** `該当なし` を選べるか。依頼意向だけは全体評価なので逃げ道を置かない */
  allowsNotApplicable: boolean;
  /** コメント欄の見出し */
  commentLabel: string;
  /** コメント欄の placeholder */
  commentPlaceholder: string;
}

/**
 * 設問 4 問。**並び順がそのまま API の `scores` / `comments` 配列の index になる**ので
 * 動かしてはいけない（server 側 `DLE_AXES` と同じ並び）。
 * @graph-connects none
 */
export const DLE_QUESTIONS: readonly DleQuestion[] = [
  {
    key: 'firstResponse',
    title: 'システムに関する質問をしたとき、DLEの回答は的確でしたか？',
    purpose:
      'ビジネスサイドがシステムに関する疑問を抱えたとき、DLEが一次窓口として的確に回答し、業務を止めずに前に進められているかを評価します。',
    scaleLabels: {
      1: '回答なし、または誤情報でPL業務に支障',
      2: '回答が曖昧・不完全でPL側のフォローが必要',
      3: '質問に正確に回答。不明時は適切に取り次ぎ',
      4: '正確な回答＋代替案や次ステップの提示',
      5: '正確な回答＋PLの業務文脈を読んだ先回りの提案',
    },
    allowsNotApplicable: true,
    commentLabel: 'システムに関する一次回答で印象に残った場面',
    commentPlaceholder: '良い面・改善面どちらでも',
  },
  {
    key: 'sparring',
    title: '要件や見積もりの相談で、DLEは判断材料やリスク・代替案を提示してくれましたか？',
    purpose:
      '要件定義・見積もり・基本設計の段階で、DLEがビジネスの目的を理解した上で技術的な選択肢やリスクを提示し、PLの意思決定を支援できているかを評価します。',
    scaleLabels: {
      1: '対応なし、または返答誤りでPJ業務に支障',
      2: '情報不足や遅れでPL側フォローが必要だった',
      3: '必要な判断材料を期限内に提供。前提を明確化',
      4: '聞かれた内容に加え、PLが想定していなかったリスクや代替案も提示した',
      5: 'PLが相談する前に、PJの検討範囲外の選択肢やリスクを先制提示した',
    },
    allowsNotApplicable: true,
    commentLabel: 'プロジェクト壁打ちで印象に残った場面',
    commentPlaceholder: '良い面・改善面どちらでも',
  },
  {
    key: 'responseSpeed',
    title: 'DLEへの質問や依頼に、業務が止まらないタイミングで対応してもらえましたか？',
    purpose:
      'DLEへの質問や依頼に対して、PLの業務が止まらないスピードで対応できているかを評価します。速さだけでなく、遅れる場合の見通し共有も含みます。',
    scaleLabels: {
      1: '対応がほぼなく、PL業務の停止・遅延を招いた',
      2: '回答に数日要した、または不完全で追加確認が必要',
      3: '1営業日内に回答し、遅れる場合は同日中に見通し報告',
      4: '1営業日内に回答し、次のアクションに必要な判断材料も併せて提示した',
      5: 'PLが依頼する前に課題を検出し、解決策を提案した',
    },
    allowsNotApplicable: true,
    commentLabel: '返答速度・レスポンスで印象に残った場面',
    commentPlaceholder: '良い面・改善面どちらでも',
  },
  {
    key: 'intent',
    title: 'DLEの活動全体を鑑みて、今後もDLEサポートを依頼したいか',
    purpose:
      'ここまでの 3 問を踏まえた総合評価です。この DLE に今後も伴走を依頼したいかという意思を、1 つの値で示してください。',
    scaleLabels: {
      1: '全く依頼したくない（信頼回復が必要な状態）',
      2: 'あまり依頼したくない（懸念あり、別DLE検討も視野）',
      3: '依頼したい（実績がありバランスの取れた相手）',
      4: '強く依頼したい（困ったら真っ先に相談したい）',
      5: 'とても強く依頼したい（このドメインにおけるCTOの代わりになりうる存在）',
    },
    // 旧 Form も依頼意向だけは「該当なし」を持たない。全体を鑑みた意思表示なので、
    // 接点が無かったという逃げ方ができない設問として扱う。
    allowsNotApplicable: false,
    commentLabel: 'DLEサポート全体へのご意見・ご要望',
    commentPlaceholder: '期待していること、変えてほしいこと',
  },
];

/**
 * 共通の評価基準（モーダル冒頭に 1 回だけ出す）。
 * 旧 Form はこれを説明文に置いていたが、設問ごとの 5 段階説明と重複して読まれていなかった。
 * @graph-connects none
 */
export const DLE_SCALE_OVERVIEW: Readonly<Record<DleScore, string>> = {
  1: '大きく下回る：対応がほぼなく、業務に支障が出た',
  2: '期待を下回る：対応はあったが、不足がありフォローが必要だった',
  3: '期待通り：依頼内容に過不足なく対応してくれた',
  4: '期待を超える：依頼に加えて、追加の工夫や提案があった',
  5: '期待を大きく超える：想定以上の価値提供があり、業務が大きく前進した',
};

/** 「該当なし」の説明。選択肢チップの tooltip に出す。 @graph-connects none */
export const DLE_NOT_APPLICABLE_NOTE =
  '評価期間中に接点が一切なかった場合のみ選択してください（集計では件数に数えません）';

/** アンケートタスク 1 件（母票 1 件 = アンケート 1 通）。 @graph-connects none */
export interface DleEvaluationTask {
  /** 母票キー。回答の一意キー */
  parentIssueKey: string;
  parentSummary: string;
  /** 回答可能になった母票ステータス */
  parentStatus: DleParentStatus;
  /** 評価対象の DLE */
  dle: string;
  /** 回答可能になった日 "YYYY-MM-DD" */
  eligibleSince: string;
  state: 'pending' | 'answered';
  responderRole: 'pl' | 'main_worker';
  /** 回答済みのときだけ入る */
  respondedBy: string | null;
  respondedAt: string | null;
}

/** GET /dle-evaluation/my-tasks のレスポンス。 @graph-connects none */
export interface MyDleTasksResponse {
  tasks: DleEvaluationTask[];
  /** 未回答件数。一括回答ボタンのバッジに出す */
  pendingCount: number;
}

/** POST /dle-evaluation/answers のリクエスト。 @graph-connects none */
export interface SubmitDleAnswerInput {
  parentIssueKey: string;
  /** 評点 4 軸。`DLE_QUESTIONS` と同じ並び。`該当なし` は null */
  scores: DleAnswerValue[];
  /** 軸ごとのコメント。評点と同じ並び。未記入は null */
  comments: (string | null)[];
}

/** POST /dle-evaluation/answers のレスポンス。 @graph-connects none */
export interface SubmitDleAnswerResponse {
  parentIssueKey: string;
  respondedAt: string;
  /** 対象月度 "YYYY/MM"（送信時刻から導出） */
  month: string;
  /** シート転記まで完了したか。false でも回答は保存済み */
  sheetSynced: boolean;
}
